import { useEffect, useRef, useState } from "react";

/**
 * Pill flotante con anillo SVG y cuenta atrás usada durante la generación de
 * un PDF. Se muestra como `toast.custom` de sonner y NO bloquea la interfaz.
 *
 * Props:
 * - eta: número de segundos previstos (mediana del historial en localStorage
 *   o 15 s por defecto). NO es una promesa: si se pasa, el timer se extiende
 *   automáticamente para no quedar en 0.
 * - label: título breve mostrado en la primera línea.
 * - state: "progress" | "done" | "error"
 * - variant: "default" (blanco+verde) | "premium" (verde profundo + dorado)
 */
export default function PdfProgressToast({
  eta,
  label = "Preparando tu informe",
  state = "progress",
  variant = "default",
}) {
  const [elapsed, setElapsed] = useState(0);
  const [adaptiveEta, setAdaptiveEta] = useState(eta);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (state !== "progress") return;
    const iv = setInterval(() => {
      const e = (Date.now() - startRef.current) / 1000;
      setElapsed(e);
      setAdaptiveEta((prev) => (e >= prev - 0.5 ? prev + 2 : prev));
    }, 200);
    return () => clearInterval(iv);
  }, [state]);

  const remaining = Math.max(0, Math.ceil(adaptiveEta - elapsed));
  const progress = Math.min(1, elapsed / Math.max(adaptiveEta, 0.1));

  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);

  const isDone = state === "done";
  const isError = state === "error";
  const isPremium = variant === "premium";

  // ---- Paleta ----
  let bg, border, ringTrack, ringColor, titleColor, subColor, badgeColor, shadow;
  if (isPremium) {
    // Variante premium: verde profundo con acento dorado (consultoría)
    bg = isDone
      ? "linear-gradient(135deg, #14532d 0%, #0F6E56 100%)"
      : isError
        ? "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)"
        : "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)";
    border = isError ? "rgba(220,38,38,0.35)" : "rgba(201,168,76,0.35)";
    ringTrack = "rgba(255,255,255,0.15)";
    ringColor = isDone ? "#E5C874" : isError ? "#fca5a5" : "#C9A84C";
    titleColor = isDone ? "#E5C874" : isError ? "#fee2e2" : "#E5C874";
    subColor = "rgba(255,255,255,0.82)";
    badgeColor = "#0b3d2e";
    shadow = "0 20px 45px -18px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.25) inset";
  } else {
    ringColor = isDone ? "#10b981" : isError ? "#ef4444" : "#0f6e56";
    bg = isDone ? "#ecfdf5" : isError ? "#fef2f2" : "#ffffff";
    border = isDone ? "#a7f3d0" : isError ? "#fecaca" : "#e2e8f0";
    ringTrack = "#e2e8f0";
    titleColor = "#0f172a";
    subColor = "#64748b";
    badgeColor = "#0f172a";
    shadow = "0 10px 30px -12px rgba(15,23,42,0.25)";
  }

  return (
    <div
      className="flex items-center gap-3 rounded-full px-4 py-3 relative overflow-hidden"
      style={{
        background: bg,
        border: `1px solid ${border}`,
        minWidth: 300,
        boxShadow: shadow,
      }}
      data-testid="pdf-progress-toast"
      data-variant={variant}
    >
      {isPremium && !isError && (
        <span
          aria-hidden
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: 2,
            background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)",
          }}
        />
      )}
      <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>
        <svg width="36" height="36" viewBox="0 0 32 32" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="16" cy="16" r={radius} stroke={ringTrack} strokeWidth="3" fill="none" />
          <circle
            cx="16"
            cy="16"
            r={radius}
            stroke={ringColor}
            strokeWidth="3"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={isDone ? 0 : offset}
            strokeLinecap="round"
            style={{
              transition:
                state === "progress"
                  ? "stroke-dashoffset 0.2s linear"
                  : "stroke-dashoffset 0.4s ease-out",
            }}
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center font-bold tabular-nums"
          style={{
            fontSize: 12,
            color: isPremium ? badgeColor : titleColor,
            background: isPremium ? "#E5C874" : "transparent",
            borderRadius: "50%",
            width: 20,
            height: 20,
            margin: "auto",
            boxShadow: isPremium ? "0 2px 6px -2px rgba(0,0,0,0.35)" : "none",
          }}
        >
          {isDone ? "✓" : isError ? "!" : remaining}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-[10px] font-bold uppercase tracking-widest mb-0.5"
          style={{ color: titleColor, letterSpacing: "0.14em" }}
        >
          {isPremium ? "Consultoría · PDF" : "PDF"}
        </div>
        <div className="text-sm font-semibold" style={{ color: isPremium ? "#ffffff" : titleColor }}>
          {isDone ? "Descarga lista" : isError ? "No pudimos generar el PDF" : label}
        </div>
        <div className="text-[11px]" style={{ color: subColor }}>
          {isDone
            ? "Guarda el archivo si te lo pide el navegador"
            : isError
              ? "Vuelve a intentarlo en unos segundos"
              : remaining > 0
                ? `~${remaining} s restantes`
                : "Casi listo…"}
        </div>
      </div>
    </div>
  );
}
